import React, { useMemo } from 'react';

import { LinearProgress } from '@material-ui/core';
import { Redirect, useLocation } from 'react-router-dom';

import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { EmployerRoute } from './EmployerRoute';

interface Props {
  /** Any-of. */
  permissions: string[];
  component: any;
  /**
   * Holding page shown in place of the screen when the permission is missing —
   * for a module the sidebar keeps showing. Without it the route redirects to
   * /dashboard, which is what a module hidden from the sidebar wants.
   */
  fallback?: React.ReactNode;
  [key: string]: any;
}

const Gate: React.FC<{ permissions: string[]; component: any; fallback?: React.ReactNode } & any> = ({
  permissions,
  component,
  fallback,
  ...props
}) => {
  const { canAny, ready } = usePermissions();
  const location = useLocation();

  // Before /users/me/basic resolves every predicate is false, so waiting here is
  // the difference between a loader and bouncing everyone off their own screen.
  if (!ready) return <LinearProgress />;

  if (!canAny(permissions)) {
    // The person IS a company admin, they just weren't given this screen. Where
    // the sidebar keeps the module, the deep link has to explain itself rather
    // than silently redirect; otherwise /dashboard, which is always reachable.
    if (fallback) return <>{fallback}</>;
    return <Redirect to={{ pathname: '/dashboard', state: { from: location.pathname } }} />;
  }

  return React.createElement(component, props);
};

/**
 * EmployerRoute plus a permission check, so the auth/admin test and its
 * /auth/login redirect stay in one place.
 */
export const PermissionRoute = ({ permissions, component, fallback, ...rest }: Props) => {
  const gated = useMemo(
    () => (props: any) =>
      <Gate permissions={permissions} component={component} fallback={fallback} {...props} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [component, permissions.join(','), fallback]
  );

  return <EmployerRoute {...rest} component={gated} />;
};
